import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EngagementModelConfigurator from './components/EngagementModelConfigurator';
import ExpertiseShowcase from './components/ExpertiseShowcase';
import ROIComparison from './components/ROIComparison';
import { trackCTAClick } from '../../utils/analyticsEvents';

const FractionalCDOServices = () => {
  const handleCTAClick = (ctaName) => {
    trackCTAClick(ctaName, 'Fractional CDO Services');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Icon name="Crown" size={20} color="white" />
              <span className="text-sm font-medium">Executive Leadership Solutions</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Fractional CDO Services
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 mb-8">
              CDO-level strategic thinking, delivered fractionally. Executive data leadership without the full-time commitment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                className="bg-white text-[#002B45] hover:bg-white/90 hover-lift"
                onClick={() => {
                  handleCTAClick('Schedule Executive Consultation');
                  window.location.href = '/contact-multi-channel-engagement-hub';
                }}
              >
                Schedule Executive Consultation
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="FileText"
                iconPosition="left"
                className="border-white text-white hover:bg-white/10"
                onClick={() => {
                  handleCTAClick('Explore Engagement Models');
                  document.getElementById('engagement-models')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Explore Engagement Models
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Value Proposition Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">When You Need Fractional CDO Leadership</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Strategic data leadership for organizations requiring executive guidance without full-time commitment
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: 'TrendingUp',
                title: 'Scaling Organizations',
                description: 'Growing companies needing strategic data leadership to support expansion without executive overhead'
              },
              {
                icon: 'Target',
                title: 'Digital Transformation',
                description: 'Organizations undergoing data transformation requiring experienced executive guidance through change'
              },
              {
                icon: 'Shield',
                title: 'Interim Leadership',
                description: 'Companies between CDO hires or needing immediate executive-level data strategy expertise'
              }
            ]?.map((scenario, index) => (
              <div key={index} className="p-6 bg-surface rounded-xl border border-border hover:shadow-elevation transition-all duration-300">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={scenario?.icon} size={24} className="text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3">{scenario?.title}</h3>
                <p className="text-muted-foreground">{scenario?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Service Overview Section */}
      <section className="py-16 px-4 bg-surface">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">Executive-Level Strategic Services</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Comprehensive CDO-level leadership covering strategy, governance, team development, and organizational transformation.
              </p>
              <div className="space-y-6">
                {[
                  {
                    icon: 'Target',
                    title: 'Strategic Planning & Roadmapping',
                    description: 'Board-level data strategy aligned with business objectives and growth plans'
                  },
                  {
                    icon: 'Users',
                    title: 'Team Development & Mentoring',
                    description: 'Elevate your existing team to think and operate at CDO level'
                  },
                  {
                    icon: 'Shield',
                    title: 'Governance & Compliance',
                    description: 'Navigate GDPR, AI Act, and data governance with expert oversight'
                  },
                  {
                    icon: 'Briefcase',
                    title: 'Vendor Management',
                    description: 'Strategic technology evaluation, selection, and partnership management'
                  }
                ]?.map((service, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Icon name={service?.icon} size={20} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">{service?.title}</h4>
                      <p className="text-muted-foreground text-sm">{service?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-elevation">
              <h3 className="text-2xl font-bold mb-6">What's Included</h3>
              <div className="space-y-4 mb-8">
                {[
                  'Monthly strategic planning sessions',
                  'Quarterly board-level reporting',
                  'Team mentoring and development',
                  'Vendor evaluation and selection',
                  'Compliance and governance oversight',
                  'Crisis management and problem-solving',
                  'Budget planning and ROI tracking',
                  '24/7 strategic consultation access'
                ]?.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-primary" />
                    <span className="text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
              <div className="p-6 bg-surface rounded-lg border border-border">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-3xl font-bold text-primary">From €5,000/Month</div>
                    <div className="text-muted-foreground">Fractional engagement</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-semibold text-secondary">70% Saving</div>
                    <div className="text-muted-foreground text-sm">vs full-time CDO</div>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  Minimum 6-month engagement. Includes all strategic services and team development.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Engagement Model Configurator */}
      <section id="engagement-models" className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Flexible Engagement Models</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Configure your engagement to match your strategic needs and organizational requirements
            </p>
          </div>
          <EngagementModelConfigurator />
        </div>
      </section>
      {/* ROI Comparison */}
      <section className="py-16 px-4 bg-surface">
        <div className="max-w-7xl mx-auto">
          <ROIComparison />
        </div>
      </section>
      {/* Expertise Showcase */}
      <section className="py-16 px-4 bg-surface">
        <div className="max-w-7xl mx-auto">
          <ExpertiseShowcase />
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready to Transform Your Data Strategy?</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule a confidential consultation to discuss how fractional CDO services can accelerate your data transformation journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              className="bg-white text-[#002B45] hover:bg-white/90 hover-lift"
              onClick={() => {
                handleCTAClick('Schedule Executive Consultation');
                window.location.href = '/contact-multi-channel-engagement-hub';
              }}
            >
              Schedule Executive Consultation
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="FileText"
              iconPosition="left"
              className="border-white text-white hover:bg-white/10"
              onClick={() => {
                handleCTAClick('Assess Your Readiness');
                window.location.href = '/data-maturity-calculator-lead-generation-assessment';
              }}
            >
              Assess Your Readiness
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FractionalCDOServices;